import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';

/// Muestra el cuerpo de un correo aislado del resto de la aplicación.
///
/// El cuerpo es HTML escrito por un tercero desconocido y se pinta dentro de la aplicación que sostiene el token de
/// sesión, así que se trata como código hostil. Hay tres capas independientes, y esta es la tercera (las otras dos,
/// el saneado y la política de contenido, están en el servidor):
///
/// `sandbox` **sin `allow-scripts`**: no se ejecuta nada dentro del marco. Esa es la garantía que no se puede
/// tocar. Y la política del propio documento lleva `script-src 'none'`, que es la misma garantía por otra vía.
///
/// ## Por qué `srcdoc` y `allow-same-origin`
///
/// El correo tiene que verse **entero**, como en cualquier webmail, y para eso el marco debe ajustarse al alto de
/// su contenido. Medir ese alto exige leer el documento de dentro, y eso solo se puede si comparte origen con la
/// página. Con `src` apuntando a la API no se puede: la API y el frontal están en origenes distintos, así que el
/// contenido queda encerrado en una caja de alto fijo con su propio desplazamiento, y el correo aparece cortado.
///
/// Con `srcdoc` y `allow-same-origin` el documento hereda el origen de la página y su alto se puede medir. Eso es
/// seguro **mientras no haya `allow-scripts`**: el riesgo de `allow-same-origin` es que un script de dentro acceda
/// al origen de la aplicación, y aquí no puede ejecutarse ninguno, ni por el sandbox ni por la política del
/// documento. Las dos condiciones van juntas: **si alguna vez se añade `allow-scripts`, hay que quitar
/// `allow-same-origin` en el mismo cambio.**
///
/// El documento llega autocontenido: las imágenes del propio mensaje vienen dentro como `data:`, así que no
/// necesita pedir nada. Para los correos demasiado grandes para viajar así, se cae a `src` (vía de reserva, con el
/// desplazamiento dentro del marco).
///
/// Por qué no `[innerHTML]`, que es lo que se hace hoy en el historial del expediente: el saneador de Angular
/// elimina las hojas de estilo, así que el correo se ve deformado; el CSS del correo choca con el de la aplicación
/// y puede romperle el diseño; no impide que se carguen las imágenes remotas, que avisan al remitente de que se ha
/// leído el correo; y no permite forzar que los enlaces salgan en pestaña nueva.
@Component({
    selector: 'app-message-body',
    templateUrl: './message-body.component.html',
    styleUrls: ['./message-body.component.css'],
    standalone: false
})
export class MessageBodyComponent implements AfterViewInit, OnChanges, OnDestroy {
  /// Documento completo y autocontenido. Es la vía normal.
  @Input() bodyDocument: string | null = null;

  /// Dirección del documento. Vía de reserva para los correos que no caben en `bodyDocument`.
  @Input() bodyUrl: string | null = null;

  @Input() blockedImageCount: number = 0;

  @Input() allowRemoteImages: boolean = false;

  /// Se emite cuando el usuario decide cargar las imágenes externas.
  @Output() allowRemoteImagesChange = new EventEmitter<boolean>();

  @ViewChild('frame') frame?: ElementRef<HTMLIFrameElement>;

  public loading: boolean = true;

  /// Alto del marco. `null` mientras no se ha podido medir: entonces ocupa el hueco disponible.
  public measuredHeight: number | null = null;

  /// True cuando el correo va por la vía de reserva y desplaza por dentro en lugar de verse entero.
  public scrollsInside: boolean = false;

  /// Vigila el alto del contenido dentro del marco. Ver `observeContent`.
  private contentObserver?: ResizeObserver;

  ngAfterViewInit() {
    this.applyContent();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['bodyDocument'] || changes['bodyUrl']) {
      this.loading = true;
      this.measuredHeight = null;
      this.applyContent();
    }
  }

  @HostListener('window:resize')
  onResize() {
    //al cambiar el ancho, el texto se recoloca y el alto deja de ser el bueno
    this.measureHeight();
  }

  onFrameLoad() {
    this.loading = false;
    this.measureHeight();
    this.observeContent();
  }

  ngOnDestroy() {
    this.contentObserver?.disconnect();
  }

  /// Sigue el alto del contenido mientras cambia.
  ///
  /// Medir solo al cargar no basta: el alto crece después, cuando el navegador termina de colocar las imágenes y
  /// las fuentes. El marco se quedaba entonces más bajo que su contenido y aparecía **una segunda barra de
  /// desplazamiento dentro del marco**, además de la de la columna. Con el observador el alto se ajusta solo.
  private observeContent() {
    this.contentObserver?.disconnect();
    if (this.scrollsInside || typeof ResizeObserver === 'undefined') return;

    const document = this.frame?.nativeElement?.contentDocument;
    if (!document?.body) return;

    this.contentObserver = new ResizeObserver(() => this.measureHeight());
    this.contentObserver.observe(document.body);
    if (document.documentElement) this.contentObserver.observe(document.documentElement);
  }

  showImages() {
    this.allowRemoteImagesChange.emit(true);
  }

  private applyContent() {
    const iframe = this.frame?.nativeElement;
    if (!iframe) return;

    if (this.bodyDocument) {
      this.scrollsInside = false;
      iframe.removeAttribute('src');
      iframe.srcdoc = this.bodyDocument;
      return;
    }

    this.scrollsInside = true;
    iframe.removeAttribute('srcdoc');

    if (!this.bodyUrl) {
      //about:blank y no cadena vacía: una cadena vacía recarga la página que contiene en algunos navegadores
      iframe.src = 'about:blank';
      this.loading = false;
      return;
    }

    iframe.src = this.bodyUrl;
  }

  /// Ajusta el alto del marco al de su contenido, para que el correo se vea entero.
  ///
  /// Solo funciona por la vía de `srcdoc`: con `src` el documento viene de la API, que está en otro origen, y el
  /// navegador no deja leerlo. En ese caso se queda sin medir y el marco desplaza por dentro.
  private measureHeight() {
    const iframe = this.frame?.nativeElement;
    if (!iframe || this.scrollsInside) return;

    try {
      const document = iframe.contentDocument;
      if (!document?.body) return;

      //se toma el mayor de todas las medidas: según cómo esté escrito el correo, el alto real aparece en el body o
      //en el elemento raíz, y con contenido flotante scrollHeight se queda corto donde offsetHeight no
      const height = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement?.scrollHeight ?? 0,
        document.documentElement?.offsetHeight ?? 0,
        Math.ceil(document.body.getBoundingClientRect().height)
      );

      //un suelo para que un correo de una línea no quede como una rendija, y un techo por si algún correo declara
      //un alto absurdo: a partir de ahí desplaza por dentro
      const nuevo = Math.min(Math.max(height + 8, 160), 20000);

      //solo si cambia de verdad: asignar el mismo valor en cada vuelta del observador provocaría un ciclo
      if (nuevo !== this.measuredHeight) this.measuredHeight = nuevo;
    } catch {
      //si el navegador no deja leerlo, se deja sin medir y el marco ocupa el hueco disponible
      this.measuredHeight = null;
    }
  }
}
